import { useEffect } from "react";
import { useRouter } from "next/router";
import { useIsMounted } from "repoV2/utils/common/render/mount";
import { ROUTES, useEventDataOfSelectedEvent } from "@Utils";
import { IEvent } from "@Interfaces";
import { IDropdownSelector } from "@Modules/common/Form/FormInputs/DropdownSelector/IDropdownSelector";
import { useSetSlots } from "@Modules/eventPage/utils/useSetSlots";
import { getCartData } from "@Modules/eventPage/utils/cart";
import { IScheduleDropdownProps, OptionType } from "./ScheduleDropdown";

const useScheduleDropdown = ({ isMobile }: IScheduleDropdownProps) => {
    const setSlots = useSetSlots();

    const router = useRouter();
    const { event, eventData } = useEventDataOfSelectedEvent();
    const {
        paymentInfo: { slots },
    }: IEvent.IStore = event;

    // Using this to only try to read CSS variables once the page has rendered on
    //  the client side, for the react-select custom styling runs initially on the server
    const clientSideRendered = useIsMounted();

    const handleSlotChange = (val: IDropdownSelector.Option | null) => {
        setSlots(val?.value ? [val.value] : []);
    };

    const options: OptionType[] = eventData?.classes?.map(
        ({ uuid, timings, slots_filled: slotsFilled }) => ({
            value: uuid,
            label: timings,
            disabled: slotsFilled >= eventData.max_spots, // disable this option
        })
    );

    useEffect(() => {
        // If eventData changes, check if the slots in the paymentInfo are of the
        // event currently opened. If not,then they need to be cleared out.
        if (router.pathname !== ROUTES.CART.urlPath) {
            if (
                eventData?.classes?.length > 0 &&
                slots?.length > 0 &&
                !eventData?.classes.map(c => c.uuid).includes(slots?.[0])
            ) {
                setSlots([]);
            }
        } else {
            // if in cart page
            const cartData = getCartData();

            const cartEventData = cartData?.listingMap?.[eventData.uuid];
            const cartSlots = cartEventData?.slots || [];
            if (
                eventData?.classes?.length > 0 &&
                cartSlots?.length > 0 &&
                !eventData?.classes.map(c => c.uuid).includes(cartSlots?.[0])
            ) {
                setSlots([]);
            } else {
                setSlots([cartSlots?.[0]]);
            }
        }
    }, [eventData]);

    const isOptionDisabled = (option: IDropdownSelector.Option) =>
        option?.disabled;

    const dropDownValue = options.filter(o =>
        slots.length ? o.value === slots[0] : null
    );

    const placeHolder = `Select a ${
        eventData?.metadata?.event?.type_name || "Class"
    }`;

    return {
        handleSlotChange,
        options,
        eventData,
        isOptionDisabled,
        dropDownValue,
        placeHolder,
        clientSideRendered,
        isMobile,
        showDropdown: !eventData.classes,
    };
};

export default useScheduleDropdown;
